import moment from 'moment';
import numeral from 'numeral';
import {
  CHART_TYPES_CONSTANTS,
  CONTRIBUTIONS,
  CURRENCIES_SIGNS,
  DATE_RANGE_FORMAT,
  DECIMALS_FORMATS,
  DISPLAY_BY,
  UNITS_DIVIDERS,
  IDEAL_UNITS_SIGNS,
  UNITS_SIGNS,
} from './constants';
import { DECIMAL_TYPES, ICurrencyFormat, IRange } from './types';

const BILLION = 1000000000;
const MILLION = 1000000;
const THOUSAND = 1000;

/**
 * To display 1000% as 1K% and 1,000,000% as 1M% we need to determine
 * what's the ideal denomination for a value.
 *
 * `getIdealDenomination(10 456 000 123) //=> { denomination: 1 000 000 000 } result: 10.46b`
 * `getIdealDenomination(1 456 000 123) //=> { denomination: 1 000 000 000 } result: 1.46b`
 * `getIdealDenomination(100 456 123) //=> { denomination: 1 000 000 } result: 100.46m`
 * `getIdealDenomination(10 456 123) //=> { denomination: 1 000 000 } result: 10.46m`
 * `getIdealDenomination(1 000 123) //=> { denomination: 1 000 000 } result: 1m`
 * `getIdealDenomination(100 123) //=> { denomination: 1 000 } result: 100.12k`
 * `getIdealDenomination(10 123) //=> { denomination: 1 000 } result: 10.12k`
 * `getIdealDenomination(1000) //=> { denomination: 1 000 } result: 1k`
 * `getIdealDenomination(100) //=> { denomination: 1 } result: 100`
 */
export const getIdealDenomination = (value: number): number => {
  const candidates = [BILLION, MILLION, THOUSAND, 1];
  const denomination: any = candidates.find(
    (candidate) => Math.abs(value) / candidate >= 1
  );
  return denomination || 1;
};

export const getFormattedCurrencyWithIdealDenomination = ({
  currencyFormat,
  value,
  isCurrencySign = false,
  isUnitSign = false,
}: {
  currencyFormat: ICurrencyFormat;
  value: number;
  isCurrencySign?: boolean;
  isUnitSign?: boolean;
}) => {
  const denomination = getIdealDenomination(value);
  const displayValue = value / denomination;
  const decimal = displayValue > 100 ? '0' : '00';
  const formattedStr = numeral(value / denomination).format(`0,0[.]${decimal}`);
  const unitSign = isUnitSign ? IDEAL_UNITS_SIGNS[denomination] : '';
  const currencySign = isCurrencySign
    ? `${CURRENCIES_SIGNS[currencyFormat.currency]}`
    : '';
  return `${currencySign}${formattedStr}${unitSign}`;
};

export const getFormattedCurrency = ({
  currencyFormat,
  value,
  isCurrencySign = false,
  isIdealDenomination = false,
  isUnitSign = false,
}: {
  currencyFormat: ICurrencyFormat;
  value: number;
  isCurrencySign?: boolean;
  isIdealDenomination?: boolean;
  isUnitSign?: boolean;
}) => {
  if (isIdealDenomination) {
    return getFormattedCurrencyWithIdealDenomination({
      currencyFormat,
      value,
      isCurrencySign,
      isUnitSign,
    });
  }
  const formattedStr = numeral(
    value / UNITS_DIVIDERS[currencyFormat.unit]
  ).format(`0,0${DECIMALS_FORMATS[currencyFormat.decimal]}`);
  const unitSign = isUnitSign ? UNITS_SIGNS[currencyFormat.unit] : '';
  const currencySign = isCurrencySign
    ? `${CURRENCIES_SIGNS[currencyFormat.currency]}`
    : '';
  return `${currencySign}${formattedStr}${unitSign}`;
};

export const getFormattedRange = (
  range: IRange,
  isCurrentYear = false
): string =>
  `${moment(range.startDate).format(DATE_RANGE_FORMAT)} - ${moment(
    range.endDate
  ).format(DATE_RANGE_FORMAT)}${
    isCurrentYear
      ? moment().isBefore(moment(range.endDate))
        ? ' (YTD)'
        : ''
      : ''
  }`;

export const getFormattedPercentage = (
  value: number | null | undefined,
  format: ICurrencyFormat
): string =>
  value === null || value === undefined
    ? '-'
    : numeral(Math.abs(value) / 100).format(
        `${DECIMALS_FORMATS[format.decimal]}%`
      );

export const getPeriodLabel = ({ startDate, endDate }: IRange): string => {
  const startDateObj = moment(startDate);
  const endDateObj = moment(endDate);
  const startYear = startDateObj.format('YYYY');
  const endYear = endDateObj.format('YYYY');
  if (startYear === endYear) {
    return startYear;
  }
  return `${startDateObj.format('YY')} - ${endDateObj.format('YY')}`;
};

export const getDateLabels = (
  currentPeriod: IRange,
  comparisonPeriod: IRange
): string[] => {
  return [getPeriodLabel(currentPeriod), getPeriodLabel(comparisonPeriod)];
};

export const currencyLabelFormatter = (
  value: number,
  format: ICurrencyFormat
): string =>
  `<strong>${getFormattedCurrency({
    currencyFormat: format,
    value,
    isIdealDenomination: true,
    isUnitSign: true,
    isCurrencySign: true,
  })}</strong>`;

export const CHART_CONFIGS = {
  [CHART_TYPES_CONSTANTS.CLUSTERED_COLUMN]: {
    labelFormatter: currencyLabelFormatter,
    stacking: undefined,
  },
  [CHART_TYPES_CONSTANTS.STACKED_COLUMN]: {
    labelFormatter: currencyLabelFormatter,
    stacking: 'normal',
  },
  [CHART_TYPES_CONSTANTS.STACKED_COLUMN_100]: {
    labelFormatter: currencyLabelFormatter,
    stacking: 'percent',
  },
};

export const tooltipValueFormatter = ({
  value,
  format,
  displayType,
  maxDecimal,
}: {
  value: number;
  format: ICurrencyFormat;
  displayType: string;
  maxDecimal: string;
}) => {
  if (displayType === DISPLAY_BY.PERCENTAGE_OF_REVENUE) {
    return getFormattedPercentage(value, {
      ...format,
      decimal: maxDecimal as keyof DECIMAL_TYPES,
    });
  }
  if (displayType === DISPLAY_BY.HEADCOUNT) {
    return value;
  }
  return getFormattedCurrency({
    value,
    currencyFormat: format,
    isCurrencySign: true,
    isIdealDenomination: true,
    isUnitSign: true,
  });
};

export const getColorIndex = (name: string) => {
  if (name === 'Contractor' || name === CONTRIBUTIONS.THIRD_PARTY) {
    return 0;
  } else if (name === 'Permanent' || name === CONTRIBUTIONS.PERSONNEL) {
    return 1;
  }
  return 0;
};
